import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ActualFormModal } from "../components/cashflow/ActualFormModal";
import { MonthlyCard } from "../components/cashflow/MonthlyCard";
import { MonthPicker } from "../components/ui/MonthPicker";
import {
  useAddActual,
  useCashflowMonthly,
  useCashflowPosition,
  useDeleteActual,
} from "../hooks/cashflow";
import type { Sector } from "../lib/accounts";
import type { ActualDto, CreateActualInput, PositionLine } from "../lib/cashflow";

const SECTORS: Sector[] = ["HAUSHALT", "IMMOBILIE", "UNZUGEORDNET"];

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function CashflowMonthly() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const month = params.get("month") ?? currentMonth();
  const monthly = useCashflowMonthly(month);
  const [quickPositionId, setQuickPositionId] = useState<string | null>(null);

  function setMonth(ym: string) {
    const next = new URLSearchParams(params);
    next.set("month", ym);
    setParams(next, { replace: true });
  }

  if (monthly.isLoading || !monthly.data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const m = monthly.data;
  const sectorBuckets = SECTORS.map((s) => ({
    sector: s,
    bucket: m.actualMonth.bySector[s],
  })).filter((x) => x.bucket !== null);

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("cashflow.monthly.title")}</h1>
        </div>
        <MonthPicker
          value={month}
          onChange={setMonth}
          aria-label={t("cashflow.monthly.month_picker") ?? undefined}
        />
      </div>

      {/* Hauptkategorien */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MonthlyCard title={t("cashflow.kind.EINNAHME")} bucket={m.actualMonth.byKind.EINNAHME} />
        <MonthlyCard title={t("cashflow.kind.AUSGABE")} bucket={m.actualMonth.byKind.AUSGABE} />
        <MonthlyCard title={t("cashflow.monthly.saldo")} bucket={m.actualMonth.total} />
      </div>

      {m.actualMonth.byKind.TRANSFER && m.actualMonth.byKind.TRANSFER.coverage.due > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MonthlyCard title={t("cashflow.kind.TRANSFER")} bucket={m.actualMonth.byKind.TRANSFER} />
          <div
            className="fv-card p-4 md:col-span-2 text-sm"
            style={{ background: "var(--border-soft)", color: "var(--muted)" }}
          >
            {t("cashflow.monthly.transfer_explainer")}
          </div>
        </div>
      )}

      {/* By Sector */}
      <section>
        <h2 className="text-base font-semibold mb-3">{t("cashflow.monthly.by_sector")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sectorBuckets.map(({ sector, bucket }) => (
            <MonthlyCard key={sector} title={t(`accounts.sector.${sector}`)} bucket={bucket} />
          ))}
        </div>
      </section>

      {/* Positions */}
      <section>
        <h2 className="text-base font-semibold mb-3">{t("cashflow.monthly.positions")}</h2>
        {m.actualMonth.positions.length === 0 ? (
          <div className="fv-card p-6 text-center" style={{ color: "var(--muted)" }}>
            {t("cashflow.monthly.no_positions")}
          </div>
        ) : (
          <div className="fv-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs"
                  style={{ background: "var(--border-soft)", color: "var(--muted)" }}
                >
                  <th className="px-3 py-2 font-semibold">{t("cashflow.monthly.column.name")}</th>
                  <th className="px-3 py-2 font-semibold">{t("cashflow.monthly.column.sector")}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t("cashflow.monthly.column.planned")}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t("cashflow.monthly.column.actual")}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t("cashflow.monthly.column.variance")}</th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {m.actualMonth.positions.map((p: PositionLine) => {
                  const overdue = Math.abs(p.actualCents) > Math.abs(p.plannedCents) * 1.1;
                  return (
                    <tr
                      key={p.id}
                      className="border-b transition-colors hover:bg-[var(--row-hover)]"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <td className="px-3 py-2.5">{p.name}</td>
                      <td className="px-3 py-2.5" style={{ color: "var(--muted)" }}>
                        {t(`accounts.sector.${p.sector}`)}
                      </td>
                      <td className="px-3 py-2.5 text-right num">{formatEur(p.plannedCents)}</td>
                      <td className="px-3 py-2.5 text-right num">{formatEur(p.actualCents)}</td>
                      <td
                        className="px-3 py-2.5 text-right num"
                        style={{
                          color: overdue ? "var(--neg)" : "var(--muted)",
                          fontWeight: overdue ? 600 : 400,
                        }}
                      >
                        {formatEur(p.varianceCents)}
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          className="text-xs font-medium"
                          style={{ color: "var(--accent)" }}
                          onClick={() => setQuickPositionId(p.id)}
                        >
                          {t("cashflow.monthly.quick_action")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Smoothed */}
      <section>
        <h2 className="text-base font-semibold mb-1">{t("cashflow.monthly.smoothed")}</h2>
        <p className="text-sm mb-3" style={{ color: "var(--muted)" }}>
          {t("cashflow.monthly.smoothed_explainer")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SECTORS.filter((s) => m.smoothed.bySector[s] !== null).map((s) => (
            <div key={s} className="fv-card p-4">
              <div className="fv-kpi-label mb-1">{t(`accounts.sector.${s}`)}</div>
              <div className="text-xl font-bold tracking-tight num">
                {formatEur(m.smoothed.bySector[s]?.plannedCents ?? 0)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {quickPositionId && (
        <QuickActualLauncher
          positionId={quickPositionId}
          defaultMonth={month}
          onClose={() => setQuickPositionId(null)}
        />
      )}
    </section>
  );
}

interface QuickActualLauncherProps {
  positionId: string;
  defaultMonth: string;
  onClose: () => void;
}

function QuickActualLauncher({ positionId, defaultMonth, onClose }: QuickActualLauncherProps) {
  const position = useCashflowPosition(positionId);
  const addActual = useAddActual();
  const deleteActual = useDeleteActual();
  if (!position.data) return null;
  return (
    <ActualFormModal
      position={position.data}
      defaultMonth={defaultMonth}
      open={true}
      onClose={onClose}
      onSubmit={(input: CreateActualInput) =>
        addActual.mutateAsync({ positionId, input }).then(() => undefined)
      }
      onReplace={async (existing: ActualDto, input: CreateActualInput) => {
        await deleteActual.mutateAsync({ positionId, actualId: existing.id });
        await addActual.mutateAsync({ positionId, input });
      }}
    />
  );
}
